import {Component, HostListener, Input, OnDestroy, OnInit} from '@angular/core';
import {NgbActiveModal} from '@ng-bootstrap/ng-bootstrap';
import {ModalDialogDismissOption} from '@shared/confirmation-dialog/modal-dialog-dismiss-option';
import {FormFieldTypes} from '@constants/pages/form-page/form-field-types';
import {DataType, FormPage, InputField} from '@models/sidebar/response/form-page';
import {AbstractControl, UntypedFormControl, UntypedFormGroup, ValidationErrors, ValidatorFn, Validators} from '@angular/forms';
import {DragulaService} from 'ng2-dragula';
import {Subscription} from 'rxjs';
import {FormPageService} from '@services/left-menu/page/form-page/form-page.service';

@Component({
    selector: 'scitis-edit-form-page',
    templateUrl: './edit-form-page.component.html',
    styleUrls: ['./edit-form-page.component.scss'],
    standalone: false
})

export class EditFormPageComponent implements OnInit, OnDestroy {
  @Input()
  public formPageConfiguration = new FormPage();
  @Input()
  public machineId: string = '';

  public formFieldTypes: FormFieldTypes[];
  public editForm: UntypedFormGroup = new UntypedFormGroup({});
  public disableBigQueryNameInput: boolean = false;
  public subFormIsValid: boolean = true;
  public dragAndDropInputFieldItems = 'INPUT_FIELD_ITEMS';
  public subscription = new Subscription();
  public tableNameIsFree: boolean = true;
  public isLoadingTableName: boolean = false;
  public subFormValidityTrigger: boolean = false;

  constructor(private activeModal: NgbActiveModal,
              private dragulaService: DragulaService,
              private formPageService: FormPageService) {
    this.formFieldTypes = Object.values(FormFieldTypes);
    this.configureDragula();
  }

  get formControls() {
    return this.editForm.controls;
  }

  static changeIndexes(inputFields: InputField[]): void {
    let index = 0;
    for (const inputField of inputFields) {
      inputField.index = index;
      index++;
    }
  }

  getTableNameClass() {
    if (this.formControls.bigQueryTableName.errors || !this.tableNameIsFree) {
      return 'is-invalid';
    }
    return 'is-valid';
  }

  getItemTypeClass(currentInputField: InputField) {
    switch (currentInputField.datatype) {
      case DataType.STRING:
        return 'fa-solid fa-text';
      case DataType.FLOAT:
        return 'fa-solid fa-calculator';
      case DataType.TIMESTAMP:
        return 'fa-solid fa-calendar';
      case DataType.BOOLEAN:
        return 'fa-solid fa-check';
      default: {
        return 'fa-solid fa-circle-question';
      }
    }
  }

  ngOnInit(): void {
    this.generateInputFieldForm();
    if (this.formPageConfiguration.tableName) {
      this.disableBigQueryNameInput = true;
    }
  }

  ngOnDestroy() {
    this.subscription.unsubscribe();
  }

  onClose(): void {
    this.activeModal.dismiss(ModalDialogDismissOption.CLOSE);
  }

  onCancel(_$event: boolean): void {
    this.activeModal.dismiss(ModalDialogDismissOption.CANCEL);
  }

  onSave() {
    if (this.editForm.invalid) {
      console.log('form is invalid');
      return;
    }
    this.setFormPageConfiguration();
    this.activeModal.close(this.formPageConfiguration);
  }

  addField(): void {
    if (!this.formPageConfiguration.inputFields) {
      this.formPageConfiguration.inputFields = [];
    }
    const inputField: InputField = new InputField();
    inputField.isCollapsed = false;
    inputField.index = this.getIndex();
    this.subFormValidityTrigger = !this.subFormValidityTrigger;
    this.formPageConfiguration.inputFields.push(inputField);
  }

  disableSaveButton(): boolean {
    return !!(this.formControls.bigQueryTableName.errors || !this.subFormIsValid || !this.formPageConfiguration.inputFields || !this.tableNameIsFree);
  }

  disableFormWhileTyping(): void {
    this.subFormIsValid = false;
  }

  onDeleteInputField(inputField: InputField): void {
    const index = this.formPageConfiguration.inputFields.indexOf(inputField);
    if (index > -1) {
      this.formPageConfiguration.inputFields.splice(index, 1);
    }
  }

  onCheckFormsValidation(formIsValid: boolean) {
    this.subFormIsValid = formIsValid;
  }

  validateTableName(value: string) {
    console.log(this.formPageConfiguration);
    if (!this.formControls.bigQueryTableName.errors && !!this.formPageConfiguration.id) {
      this.isLoadingTableName = true;
      this.formPageService.validateTableName(this.machineId, this.formPageConfiguration.id, value).subscribe((tableNameIsFree: boolean) => {
        this.tableNameIsFree = tableNameIsFree;
        this.isLoadingTableName = false;
      }, (error) => {
        console.log(error);
        this.isLoadingTableName = false;
      });
    }
  }

  @HostListener('document:keydown.escape', ['$event']) onKeydownHandler(_event: KeyboardEvent) {
    this.dragulaService.destroy(this.dragAndDropInputFieldItems);
  }

  isCollapsed(collapsed: boolean | undefined) {
    if (collapsed === undefined) {
      return true;
    }
    return collapsed;
  }

  private getIndex(): number {
    if (!this.formPageConfiguration.inputFields || this.formPageConfiguration.inputFields.length <= 0) {
      return 0;
    }
    return this.formPageConfiguration.inputFields.length;
  }

  private setFormPageConfiguration(): void {
    this.formPageConfiguration.tableName = this.formControls.bigQueryTableName.value;
    if (this.formPageConfiguration.inputFields) {
      for (const inputField of this.formPageConfiguration.inputFields) {
        delete inputField.isCollapsed;
        delete inputField.formFieldType;
        delete inputField.isValidConfiguration;
      }
    }
  }

  private generateInputFieldForm(): void {
    this.editForm = new UntypedFormGroup({
      bigQueryTableName: new UntypedFormControl(this.formPageConfiguration.tableName, [Validators.required, this.reservedWordsValidator(), this.validBigQueryNameValidator()])
    });
  }

  private reservedWordsValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const equal = this.isReservedWord(control.value);
      return equal ? {reservedWord: {value: control.value}} : null;
    };
  }

  private isReservedWord(bigQueryNameInput: string): boolean {
    if (!bigQueryNameInput) {
      return false;
    }
    const reservedWords: string[] = ['ALL', 'AND', 'ANY', 'ARRAY', 'AS', 'ASC', 'ASSERT_ROWS_MODIFIED', 'AT', 'BETWEEN', 'BY', 'CASE', 'CAST',
      'COLLATE', 'CONTAINS', 'CREATE', 'CROSS', 'CUBE', 'CURRENT', 'DEFAULT', 'DEFINE', 'DESC', 'DISTINCT', 'ELSE', 'END', 'ENUM', 'ESCAPE', 'EXCEPT',
      'EXCLUDE', 'EXISTS', 'EXTRACT', 'FALSE', 'FETCH', 'FOLLOWING', 'FOR', 'FROM', 'FULL', 'GROUP', 'GROUPING', 'GROUPS', 'HASH', 'HAVING', 'IF',
      'IGNORE', 'IN', 'INNER', 'INTERSECT', 'INTERVAL', 'INTO', 'IS', 'JOIN', 'LATERAL', 'LEFT', 'LIKE', 'LIMIT', 'LOOKUP', 'MERGE', 'NATURAL', 'NEW',
      'NO', 'NOT', 'NULL', 'NULLS', 'OF', 'ON', 'OR', 'ORDER', 'OUTER', 'OVER', 'PARTITION', 'PRECEDING', 'PROTO', 'RANGE', 'RECURSIVE', 'RESPECT',
      'RIGHT', 'ROLLUP', 'ROWS', 'SELECT', 'SET', 'SOME', 'STRUCT', 'TABLE', 'TABLESAMPLE', 'THEN', 'TO', 'TREAT', 'TRUE', 'UNBOUNDED', 'UNION', 'UNNEST',
      'USING', 'WHEN', 'WHERE', 'WINDOW', 'WITH', 'WITHIN'];

    return reservedWords.includes(bigQueryNameInput.toUpperCase());
  }

  private validBigQueryNameValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const equal = this.formPageService.bigQueryCompatibleName(control.value);
      return equal ? null : {bigQueryValidName: {value: control.value}};
    };
  }

  private configureDragula(): void {
    this.subscription.add(this.dragulaService.drag(this.dragAndDropInputFieldItems)
        .subscribe(({el}) => {
          el.classList.remove('ex-moved');
          this.subFormValidityTrigger = !this.subFormValidityTrigger;
        })
    );

    this.subscription.add(this.dragulaService.drop(this.dragAndDropInputFieldItems)
        .subscribe(({el}) => {
          el.classList.add('ex-moved');
          EditFormPageComponent.changeIndexes(this.formPageConfiguration.inputFields);
        })
    );

    this.subscription.add(this.dragulaService.over(this.dragAndDropInputFieldItems)
        .subscribe(({el, container}) => {
          container.classList.add('ex-over');
        })
    );
    this.subscription.add(this.dragulaService.out(this.dragAndDropInputFieldItems)
        .subscribe(({el, container}) => {
          container.classList.remove('ex-over');
        })
    );
  }
}
